/**
 * @name rfidReaderUtils
 * @description Utilitários para captura e processamento de dados de leitores RFID físicos
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/utils/rfidReaderUtils.ts
 */

/**
 * Interface para configuração do leitor RFID
 */
export interface RfidReaderConfig {
  debounceTime: number; // Tempo de debounce para evitar leituras duplas
  autoSubmitDelay: number; // Tempo para auto-submit após receber dados
  minLength: number; // Comprimento mínimo esperado do código
  maxLength: number; // Comprimento máximo esperado do código
  terminators: string[]; // Caracteres que indicam fim da leitura
  clearOnSuccess: boolean; // Limpar campo após identificação bem-sucedida
}

/**
 * Configuração padrão para leitores RFID
 */
export const defaultRfidConfig: RfidReaderConfig = {
  debounceTime: 100, // 100ms para evitar leituras duplicadas
  autoSubmitDelay: 500, // 500ms após receber dados completos
  minLength: 3, // Mínimo 3 caracteres
  maxLength: 20, // Máximo 20 caracteres
  terminators: ["\r", "\n", "\t"], // Enter, newline, tab
  clearOnSuccess: true,
};

/**
 * Classe para gerenciar a captura de dados do leitor RFID
 */
export class RfidReaderCapture {
  private buffer: string = "";
  private lastInputTime: number = 0;
  private config: RfidReaderConfig;
  private timeoutId: NodeJS.Timeout | null = null;
  private onDataReceived: ((data: string) => void) | null = null;

  constructor(config: Partial<RfidReaderConfig> = {}) {
    this.config = { ...defaultRfidConfig, ...config };
  }

  /**
   * Define callback para quando dados completos são recebidos
   */
  setOnDataReceived(callback: (data: string) => void) {
    this.onDataReceived = callback;
  }

  /**
   * Processa entrada de caractere do leitor RFID
   */
  processInput(char: string): void {
    const now = Date.now();

    // Se passou muito tempo desde a última entrada, limpa o buffer
    if (now - this.lastInputTime > this.config.debounceTime * 10) {
      this.buffer = "";
      console.log("🔄 Buffer RFID limpo por timeout");
    }

    this.lastInputTime = now;

    // Verifica se é um caractere terminador
    if (this.config.terminators.includes(char)) {
      console.log("⏹️ Caractere terminador detectado:", char.charCodeAt(0));
      this.processCompleteInput();
      return;
    }

    // Adiciona caractere ao buffer
    this.buffer += char;
    console.log("📡 RFID buffer atualizado:", {
      buffer: this.buffer,
      length: this.buffer.length,
      char: char,
      charCode: char.charCodeAt(0),
    });

    // Limpa timeout anterior
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    // Define novo timeout para auto-submit
    this.timeoutId = setTimeout(() => {
      if (this.buffer.length >= this.config.minLength) {
        console.log("⏰ Auto-submit por timeout");
        this.processCompleteInput();
      }
    }, this.config.autoSubmitDelay);
  }

  /**
   * Processa entrada completa do RFID
   */
  private processCompleteInput(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    const data = this.buffer.trim();

    console.log("✅ Entrada RFID completa capturada:", {
      data,
      length: data.length,
      isValid:
        data.length >= this.config.minLength &&
        data.length <= this.config.maxLength,
    });

    if (
      data.length >= this.config.minLength &&
      data.length <= this.config.maxLength
    ) {
      if (this.onDataReceived) {
        this.onDataReceived(data);
      }
    } else {
      console.warn("⚠️ Dados RFID fora do comprimento esperado:", {
        received: data.length,
        expected: `${this.config.minLength}-${this.config.maxLength}`,
      });
    }

    this.buffer = "";
  }

  /**
   * Limpa o buffer atual
   */
  clearBuffer(): void {
    this.buffer = "";
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    console.log("🧹 Buffer RFID limpo manualmente");
  }

  /**
   * Obtém o estado atual do buffer
   */
  getBufferState(): { buffer: string; length: number; isReading: boolean } {
    return {
      buffer: this.buffer,
      length: this.buffer.length,
      isReading: this.timeoutId !== null,
    };
  }
}

// Adicionar React ao escopo se não estiver disponível
// const React = (globalThis as any).React || require('react');

export default {
  RfidReaderCapture,
  defaultRfidConfig,
};
